import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { css } from '@emotion/react'

import { useMarket } from '@/utils/multi-markets/context'
import { Market } from '@/utils/multi-markets/types'
import { Locale } from '@/utils/i18n/types'
import { colors, border, transitions } from '@/styles/theme'
import { mediaUp } from '@/styles/media'
import { H3Styles, Text2Styles } from '@/styles/typography'
import { useIsMobile } from '@/utils/useMobile'
import { getFlagByMarket } from '@/constants/flagAssets'

import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'
import { toAbsoluteURL, translatePathname } from '@/utils/multi-markets/common'
import { useFetchCustomizedClients } from '@/service/apiHooks/useFetchCustomizedClients'

interface Props {
  show: boolean
  onClose(): void
}

function LangBottomSheet({
  show,
  onClose,
  children
}: {
  show: boolean
  onClose: () => void
  children: React.ReactNode
}) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [show])

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose()
    }
  }

  if (!show) return null

  return (
    <div css={overlayStyle} ref={overlayRef} onClick={handleOverlayClick}>
      <div css={bottomSheetStyle} ref={sheetRef}>
        <div css={handleBarStyle} />
        <div css={sheetContentStyle}>{children}</div>
      </div>
    </div>
  )
}

function LangLink(props: Props) {
  const { t } = useTranslation('common')
  const { show = false, onClose } = props
  const router = useRouter()
  const { market, locale, markets } = useMarket()
  const { isMobile } = useIsMobile()

  const [currentLang, setCurrentLang] = useState<Locale>(locale)
  const [currentMarket, setCurrentMarket] = useState<Market>(market)
  const [loading, setLoading] = useState(false)
  const { id } = router.query as any
  const { clientDetails } = useFetchCustomizedClients({
    id
  })

  const hideLangs = router.pathname === '/intermobility-expo'
  const isLegia = router.pathname === '/legia'

  useEffect(() => {
    if (!show) {
      setCurrentLang(locale)
      setCurrentMarket(market)
      setLoading(false)
    }
  }, [show])

  const changeLang = (lang: Locale) => {
    setCurrentLang(lang)
  }

  const changeMarket = (region: Market) => {
    if (currentLang === 'ar' && region === 'pl') {
      changeLang('en')
    }
    setCurrentMarket(region)
  }

  const saveLangSettings = useCallback(() => {
    setLoading(true)
    const { asPath, pathname, locale: currentLocale, query } = router

    if (currentMarket !== market) {
      const absoluteUrl = toAbsoluteURL(
        asPath,
        pathname,
        currentLang,
        markets[currentMarket]
      )
      window.location.href = absoluteUrl
    } else {
      if (currentLang !== currentLocale) {
        const translatedPath = translatePathname(
          asPath,
          pathname,
          currentLang
        ).split('?')[0]
        router.push(
          {
            pathname: translatedPath,
            query: query
          },
          undefined,
          { locale: currentLang }
        )
      } else {
        setLoading(false)
        onClose()
      }
    }
  }, [currentLang, currentMarket, market, router.query])

  const countries = (
    <>
      <div css={label}>{t('languageModal.countryLabel')}</div>
      <button
        css={[pickButton, currentMarket === 'pl' && pickButtonActive]}
        onClick={() => changeMarket('pl')}
      >
        <img src={getFlagByMarket('pl')} alt="Poland flag" css={flag} />
        <div css={country}>{t('languageModal.countryPL')}&nbsp;</div>
        <div css={currency}>PLN</div>
      </button>
      <button
        css={[pickButton, currentMarket === 'ae' && pickButtonActive]}
        onClick={() => changeMarket('ae')}
      >
        <img src={getFlagByMarket('ae')} alt="UAE flag" css={flag} />
        <div css={country}>{t('languageModal.countryAE')}&nbsp;</div>
        <div css={currency}>AED</div>
      </button>
      <button
        css={[pickButton, currentMarket === 'bh' && pickButtonActive]}
        onClick={() => changeMarket('bh')}
      >
        <img src={getFlagByMarket('bh')} alt="Bahrain flag" css={flag} />
        <div css={country}>{t('languageModal.countryBH')}&nbsp;</div>
        <div css={currency}>BHD</div>
      </button>
    </>
  )

  const langs = (
    <>
      <div css={label}>{t('languageModal.languageLabel')}</div>
      {!hideLangs && (
        <button
          css={[pickButton, currentLang === 'pl' && pickButtonActive]}
          onClick={() => changeLang('pl')}
        >
          <div css={country}>Polski</div>
        </button>
      )}
      <button
        css={[pickButton, currentLang === 'en' && pickButtonActive]}
        onClick={() => changeLang('en')}
      >
        <div css={country}>English</div>
      </button>
      {!hideLangs && (
        <>
          <button
            css={[pickButton, currentLang === 'de' && pickButtonActive]}
            onClick={() => changeLang('de')}
          >
            <div css={country}>Deutsch</div>
          </button>
          <button
            css={[pickButton, currentLang === 'ar' && pickButtonActive]}
            onClick={() => changeLang('ar')}
            disabled={currentMarket === 'pl'}
          >
            <div css={[country]}>عربي</div>
          </button>
        </>
      )}
    </>
  )

  const ModalContent = (
    <>
      <div css={content}>
        {!clientDetails?.free && !isLegia && <div css={row}>{countries}</div>}
        <div css={row}>{langs}</div>
      </div>
      <Button
        text={t('languageModal.save')}
        css={buttonWrapper}
        onClick={saveLangSettings}
        loading={loading}
      />
    </>
  )

  if (isMobile) {
    return (
      <LangBottomSheet show={show} onClose={onClose}>
        {ModalContent}
      </LangBottomSheet>
    )
  }

  return (
    <Modal onClose={onClose} show={show}>
      {ModalContent}
    </Modal>
  )
}

const overlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const bottomSheetStyle = css`
  background: white;
  width: 100%;
  max-width: 500px;
  max-height: 85vh;
  border-radius: 16px 16px 0 0;
  animation: slideUp 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`

const handleBarStyle = css`
  width: 40px;
  height: 4px;
  background: #ccc;
  border-radius: 2px;
  margin: 12px auto 8px;
  flex-shrink: 0;
`

const sheetContentStyle = css`
  padding: 0 20px 20px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
`

const content = css`
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: -18px;

  ${mediaUp('lg')} {
    margin-bottom: -40px;
  }
`

const row = css`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;

  &:first-of-type {
    margin-bottom: 20px;

    ${mediaUp('lg')} {
      margin-bottom: 20px;
    }
  }
`

const label = css`
  ${H3Styles};
  width: 100%;
  margin-bottom: 20px;

  ${mediaUp('lg')} {
    margin-bottom: 40px;
  }
`

const pickButton = css`
  display: flex;
  align-items: center;
  margin-bottom: 18px;
  border-radius: 28px;
  box-shadow: ${border.shadow};
  background-color: ${colors.white};
  transition: background-color ${transitions.base};
  border: 2px solid transparent;
  padding: 0 18px;
  height: 30px;
  width: 100%;

  ${mediaUp('md')} {
    width: calc(50% - 10px);
  }

  ${mediaUp('lg')} {
    margin-bottom: 40px;
    padding: 0 40px;
    height: 55px;
  }

  &[disabled] {
    background-color: ${colors.cloudyBlue};
  }
`

const pickButtonActive = css`
  border-color: ${colors.darkSkyBlue};
  background-color: ${colors.paleGrey};
`

const flag = css`
  margin-inline-end: 10px;
  width: 21px;
  height: 14px;

  ${mediaUp('lg')} {
    margin-inline-end: 18px;
    width: 32px;
    height: 21px;
  }
`

const country = css`
  ${Text2Styles};
  color: ${colors.twilightBlue};
  transition: color ${transitions.base};

  [disabled] & {
    color: ${colors.white};
  }
`

const currency = css`
  ${Text2Styles};
`

const buttonWrapper = css`
  margin: 40px 0 auto;
  width: 100%;

  ${mediaUp('lg')} {
    width: auto;
    margin: 80px auto 0;
  }
`

const arButton = css`
  justify-content: end;
`

export default LangLink
